/**
 * Web 全局搜索（main-api 方案 docs/superpowers/specs/2026-10-22-global-search-design.md，Frank 2026-10-23 定稿）。
 * 顶栏一个搜索框，⌘K / Ctrl+K（或光标不在输入框里时按 `/`）打开面板：输入名字、代码、确认码、编号或 id，
 * 按类型分组列出候选，回车打开（flyout 或跳页面），⌘Enter 新标签页打开。第一组「页面」在本地过滤侧栏菜单。
 * 不做全文搜索（任务标题等留在各列表页的 `q`）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Command as CommandPrimitive } from 'cmdk'
import { Clock, Search, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { useOpenEntity } from '@/components/entity-flyout/useOpenEntity'
import { useAssistantWindow } from '@/components/ai-chat/assistantWindowContext'
import { GENERAL_ENTRY } from '@/features/ai-chat/assistantSessions'
import { deriveNavGroups, filterNavGroups, normalizePath } from '@/components/sidebar/nav-model'
import { pickI18n, useLocale } from '@/hooks/useLocale'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setActiveTab } from '@/store/slices/navigationSlice'
import type { EntityRef } from '@/api/entitiesApi'
import type { MenuItem } from '@/types/menu'
import { cn } from '@/lib/utils'
import {
  fullPagePath, GROUP_ORDER, PAGE_ICON, SEARCH_TYPES, type EntitySearchType, type SearchGroupKey,
} from './searchTypes'
import { MIN_CHARS, SHOWN_PER_TYPE, useEntitySearch } from './useEntitySearch'
import { pushRecent, readRecent, type RecentItem } from './recent'

const MAX_PAGES = 6
/** 「正在查找…」至少等这么久才出现，免得快的请求一闪 */
const LOADING_DELAY_MS = 300

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)
}

/** 名字里命中的第一处加粗（不分大小写） */
function Highlight({ text, query }: { text: string; query: string }) {
  const i = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1
  if (i < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <strong className="font-semibold text-foreground">{text.slice(i, i + query.length)}</strong>
      {text.slice(i + query.length)}
    </>
  )
}

function useDelayedFlag(on: boolean, delayMs: number): boolean {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!on) {
      setShown(false)
      return
    }
    const timer = setTimeout(() => setShown(true), delayMs)
    return () => clearTimeout(timer)
  }, [on, delayMs])
  return shown
}

interface PageHit {
  item: MenuItem
  label: string
  section: string
}

function usePages(query: string): { matches: PageHit[]; common: PageHit[] } {
  const menuTree = useAppSelector((state) => state.auth.menuTree)
  const locale = useLocale()
  const groups = useMemo(() => deriveNavGroups(menuTree), [menuTree])
  return useMemo(() => {
    const hits = (gs: typeof groups): PageHit[] => {
      const seen = new Set<string>()
      return gs.flatMap((g) => g.items
        .filter((it) => it.routePath && it.isActive !== false && it.routePath !== '/logout')
        .filter((it) => !seen.has(String(it.id)) && seen.add(String(it.id)))
        .map((it) => ({ item: it, label: pickI18n(it.labelI18n, locale, it.name), section: pickI18n(g.labelI18n, locale, g.label) })))
    }
    // 常用页面 = 侧栏钉住的第一组（My Workspace）
    const pinned = groups.length > 0 && groups[0].label ? [groups[0]] : []
    return {
      matches: query ? hits(filterNavGroups(groups, query)).slice(0, MAX_PAGES) : [],
      common: hits(pinned).slice(0, MAX_PAGES),
    }
  }, [groups, locale, query])
}

interface EntityGroup {
  key: SearchGroupKey
  exact: boolean
}

/**
 * 组顺序：有完全命中的组在前；其余「页面」在前，实体组按后端回来的顺序（它已按命中档次排：词首命中的房源排在
 * 只在词中间命中的客人名前面）。`GROUP_ORDER` 只在后端顺序相同时兜底。
 */
export function orderGroups(groups: EntityGroup[], backendOrder: readonly string[]): EntityGroup[] {
  const rank = (key: SearchGroupKey) => (key === 'page' ? -1 : backendOrder.indexOf(key))
  return [...groups].sort((a, b) => Number(b.exact) - Number(a.exact) || rank(a.key) - rank(b.key)
    || GROUP_ORDER.indexOf(a.key) - GROUP_ORDER.indexOf(b.key))
}

function GlobalSearchPanel({ onClose, escapeRef }: {
  onClose: () => void
  /** Esc：有输入时先清空（返回 true = 已处理，面板不关） */
  escapeRef: React.MutableRefObject<(() => boolean) | null>
}) {
  const { t } = useTranslation('globalSearch')
  // 第二行说明里的状态、地点种类借用这几个模块已有的译文（按需加载的 namespace，要在这里点名）
  const { t: tDetail } = useTranslation(['reservations', 'assets', 'listings'])
  const [input, setInput] = useState('')
  const [selected, setSelected] = useState('')
  const query = input.trim()
  escapeRef.current = () => {
    if (!input) return false
    setInput('')
    return true
  }
  const userId = useAppSelector((state) => state.user.userId) ?? ''
  const search = useEntitySearch(input, userId)
  const showLoading = useDelayedFlag(search.status === 'loading', LOADING_DELAY_MS)
  const { matches: pages, common: commonPages } = usePages(query)
  const recent = useMemo(() => readRecent(userId), [userId])
  const timezone = useUserTimezone()
  const openEntity = useOpenEntity()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { openAssistant } = useAssistantWindow()

  // 选中项 value → 回车 / ⌘Enter 做什么。每次渲染重建（只有几十项）
  const actions = useRef(new Map<string, { open: () => void; newTab?: () => void }>())
  actions.current = new Map()

  const register = (value: string, open: () => void, newTabPath?: string | null) => {
    actions.current.set(value, { open, newTab: newTabPath ? () => window.open(newTabPath, '_blank', 'noopener') : undefined })
    return value
  }

  const openRecord = useCallback((type: EntitySearchType, id: string, label: string) => {
    pushRecent(userId, { type, id, label })
    onClose()
    openEntity(type, id)
  }, [onClose, openEntity, userId])

  const openPage = useCallback((item: MenuItem) => {
    onClose()
    dispatch(setActiveTab(String(item.id)))
    void navigate(normalizePath(item.routePath || ''))
  }, [dispatch, navigate, onClose])

  const go = useCallback((path: string) => {
    onClose()
    void navigate(path)
  }, [navigate, onClose])

  const byType = useMemo(() => {
    const map = new Map<EntitySearchType, EntityRef[]>()
    for (const ref of search.refs) {
      const type = ref.entityType as EntitySearchType
      map.set(type, [...(map.get(type) ?? []), ref])
    }
    return map
  }, [search.refs])

  const pagesExact = pages.some((p) => p.label.toLowerCase() === query.toLowerCase())
  const groups = orderGroups([
    ...(pages.length ? [{ key: 'page' as const, exact: pagesExact }] : []),
    ...[...byType].map(([type, refs]) => ({ key: type, exact: refs.some((r) => r.exact) })),
  ], [...byType.keys()])

  const searching = query.length >= MIN_CHARS
  const nothingFound = searching && search.status === 'done' && search.refs.length === 0 && pages.length === 0

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      const action = actions.current.get(selected)
      if (action?.newTab) {
        e.preventDefault()
        action.newTab()
      }
    }
  }

  const renderRecord = (type: EntitySearchType, ref: { entityId: string; label: string; exact?: boolean; detail?: Record<string, unknown> | null }, valuePrefix = '') => {
    const def = SEARCH_TYPES[type]
    const Icon = def.icon
    const detail = ref.detail && def.detail ? def.detail(ref.detail, { t: tDetail, timezone }) : null
    const value = register(`${valuePrefix}${type}:${ref.entityId}`, () => openRecord(type, ref.entityId, ref.label),
      fullPagePath(type, ref.entityId))
    return (
      <CommandItem key={value} value={value} onSelect={() => actions.current.get(value)?.open()}
        className="items-start gap-3" data-testid="global-search-item">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-foreground"><Highlight text={ref.label} query={query} /></div>
          {detail && <div className="truncate text-xs text-muted-foreground">{detail}</div>}
        </div>
        {ref.exact && (
          <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
            {t('exact')}
          </span>
        )}
      </CommandItem>
    )
  }

  const renderPage = (page: PageHit, valuePrefix: string) => {
    const value = register(`${valuePrefix}${page.item.id}`, () => openPage(page.item), normalizePath(page.item.routePath || ''))
    return (
      <CommandItem key={value} value={value} onSelect={() => actions.current.get(value)?.open()} className="gap-3"
        data-testid="global-search-page">
        <PAGE_ICON className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground"><Highlight text={page.label} query={query} /></span>
        {page.section && <span className="shrink-0 text-xs text-muted-foreground">{page.section}</span>}
      </CommandItem>
    )
  }

  return (
    <Command shouldFilter={false} loop value={selected} onValueChange={setSelected} onKeyDown={onKeyDown}
      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
      <div className="flex items-center border-b border-border px-3" cmdk-input-wrapper="">
        <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
        <CommandPrimitive.Input
          value={input}
          onValueChange={setInput}
          placeholder={t('input.placeholder')}
          aria-label={t('input.placeholder')}
          className="flex h-12 w-full bg-transparent py-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
          data-testid="global-search-input"
        />
        {/* 窄屏全屏、没有 Esc：给一个取消 */}
        <button type="button" onClick={onClose} className="ml-2 shrink-0 text-sm text-primary sm:hidden">
          {t('dialog.cancel')}
        </button>
      </div>
      <CommandList className="max-h-[min(60vh,520px)] p-1">
        {!query && (
          <>
            {recent.length > 0 && (
              <CommandGroup heading={t('groups.recent')}>
                {recent.map((r: RecentItem) => {
                  const value = register(`recent:${r.type}:${r.id}`, () => openRecord(r.type, r.id, r.label), fullPagePath(r.type, r.id))
                  const Icon = SEARCH_TYPES[r.type].icon
                  return (
                    <CommandItem key={value} value={value} onSelect={() => actions.current.get(value)?.open()} className="gap-3"
                      data-testid="global-search-recent">
                      <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{r.label}</span>
                      <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                        <Icon className="h-3.5 w-3.5" />{t(`types.${r.type}`)}
                      </span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
            {commonPages.length > 0 && (
              <CommandGroup heading={t('groups.commonPages')}>{commonPages.map((p) => renderPage(p, 'common:'))}</CommandGroup>
            )}
            <p className="px-3 py-2 text-xs text-muted-foreground">{t('hint.whatToSearch')}</p>
          </>
        )}

        {groups.map(({ key }) => {
          if (key === 'page') {
            return <CommandGroup key="page" heading={t('types.page')}>{pages.map((p) => renderPage(p, 'page:'))}</CommandGroup>
          }
          const refs = byType.get(key) ?? []
          const def = SEARCH_TYPES[key]
          const more = refs.length > SHOWN_PER_TYPE && def.moreHref ? def.moreHref(query) : null
          return (
            <CommandGroup key={key} heading={t(`types.${key}`)}>
              {refs.slice(0, SHOWN_PER_TYPE).map((ref) => renderRecord(key, ref))}
              {more && (
                <CommandItem value={register(`more:${key}`, () => go(more))} onSelect={() => go(more)}
                  className="text-xs text-muted-foreground">
                  {t('more')}
                </CommandItem>
              )}
            </CommandGroup>
          )
        })}

        {query && !searching && pages.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t('hint.tooShort', { count: MIN_CHARS })}</p>
        )}
        {searching && search.status === 'loading' && showLoading && (
          <p className="px-3 py-2 text-sm text-muted-foreground" data-testid="global-search-loading">{t('loading')}</p>
        )}
        {searching && search.status === 'error' && (
          <div className="flex items-center justify-between gap-3 px-3 py-3 text-sm" data-testid="global-search-error">
            <span className="text-muted-foreground">{t('error.title')}</span>
            <Button variant="outline" size="sm" onClick={search.retry}>{t('error.retry')}</Button>
          </div>
        )}
        {nothingFound && (
          <div className="space-y-3 px-3 py-6 text-center" data-testid="global-search-empty">
            <p className="text-sm text-foreground">{t('empty.title', { query })}</p>
            <p className="text-xs text-muted-foreground">{t('hint.whatToSearch')}</p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => go(`/tasks?q=${encodeURIComponent(query)}`)}>
                <Search className="mr-1.5 h-3.5 w-3.5" />{t('empty.searchTasks', { query })}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { onClose(); openAssistant({ entry: GENERAL_ENTRY, startMessage: query }) }}>
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />{t('empty.askAssistant')}
              </Button>
            </div>
          </div>
        )}
      </CommandList>
      <div className="hidden items-center gap-4 border-t border-border px-3 py-2 text-[11px] text-muted-foreground sm:flex">
        <span>{t('footer.move')}</span>
        <span>{t('footer.open')}</span>
        <span>{t('footer.newTab', { key: isMac() ? '⌘' : 'Ctrl' })}</span>
        <span>{t('footer.close')}</span>
      </div>
    </Command>
  )
}

/**
 * 顶栏入口 + 面板。`compact`：窄屏只显示放大镜图标。
 * ⌘K / Ctrl+K 开关面板；`/` 在光标不在输入框里时打开。页面自己先处理了这个键（`defaultPrevented`，如知识库页的标题跳转）就不抢。
 */
export function GlobalSearch({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation('globalSearch')
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const escapeRef = useRef<(() => boolean) | null>(null)
  const shortcut = isMac() ? '⌘K' : 'Ctrl K'

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((v) => !v)
      } else if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault()
        setOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <>
      {compact ? (
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label={t('trigger.aria')}
          className="h-9 w-9 text-muted-foreground hover:bg-accent hover:text-foreground" data-testid="global-search-trigger">
          <Search className="h-4 w-4" />
        </Button>
      ) : (
        <button type="button" onClick={() => setOpen(true)} aria-label={t('trigger.aria')} data-testid="global-search-trigger"
          className="flex h-9 w-full items-center gap-2 rounded-md border border-border bg-muted px-3 text-sm text-muted-foreground transition-colors hover:bg-accent">
          <Search className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate text-left">{t('trigger.placeholder')}</span>
          <kbd className="shrink-0 rounded border border-border bg-background px-1.5 py-0.5 font-sans text-[11px] text-muted-foreground">
            {shortcut}
          </kbd>
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          hideClose
          className={cn(
            'top-[12vh] max-w-[640px] translate-y-0 gap-0 overflow-hidden p-0',
            'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
            // 窄屏：几乎占满宽度、离顶 72px——手机浏览器顶上固定有 56px 的「Open in Nova One」横幅（z 1000），全屏会被它盖住输入框
            'max-sm:top-[72px] max-sm:w-[calc(100%-16px)] max-sm:rounded-lg',
          )}
          onEscapeKeyDown={(e) => { if (escapeRef.current?.()) e.preventDefault() }}
          data-testid="global-search-dialog"
        >
          <DialogTitle className="sr-only">{t('dialog.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('hint.whatToSearch')}</DialogDescription>
          {open && <GlobalSearchPanel onClose={close} escapeRef={escapeRef} />}
        </DialogContent>
      </Dialog>
    </>
  )
}
